import { BREAKPOINT } from "constants/theme";
import { Theme, ThemeColors } from "styled";
import defaultTheme from "./themes/default";
import * as navyTheme from "./themes/navy";
import * as ufcuTheme from "./themes/ufcu";

// RESPONSIVE

export const BREAKPOINTS = {
  [BREAKPOINT.sm]: 0,
  [BREAKPOINT.md]: 576,
  [BREAKPOINT.lg]: 1024,
  [BREAKPOINT.xl]: 1280,
};

const breakpointArray = [
  BREAKPOINT.sm,
  BREAKPOINT.md,
  BREAKPOINT.lg,
  BREAKPOINT.xl,
];

export const media = (breakpoint: string) => {
  const index = breakpointArray.indexOf(breakpoint);
  const query = ["@media screen"];
  if (index > 0) {
    query.push(`and (min-width: ${BREAKPOINTS[breakpointArray[index]]}px)`);
  }
  if (index < breakpointArray.length - 1) {
    query.push(
      `and (max-width: ${BREAKPOINTS[breakpointArray[index + 1]] - 1}px)`
    );
  }
  return query.join(" ");
};

export const mediaMin = (breakpoint: string) =>
  `@media screen and (min-width: ${BREAKPOINTS[breakpoint]}px)`;

export const mediaMax = (breakpoint: string) =>
  `@media screen and (max-width: ${BREAKPOINTS[breakpoint] - 1}px)`;

export const generateMediaQueries = (styleArray: Array<string>) =>
  breakpointArray
    .map(
      (bp, index) => `${media(bp)} { 
    ${styleArray[index]}
  }`
    )
    .join(" ");

const THEME_MAP: { [slug: string]: Theme } = {
  ufcu: ufcuTheme,
  navy: navyTheme,
};

export const getTheme = (slug?: string) => {
  const baseTheme = THEME_MAP[slug] || defaultTheme;

  const fallbackColors = {
    // TODO: Define better fallback colors
    tertiary: baseTheme.colors.primary,
    accent: baseTheme.colors.primary,
    highlight: baseTheme.colors.primary,
  };

  const colors = {
    ...defaultTheme.colors,
    ...fallbackColors,
    ...baseTheme.colors,
  };
  colors.shadow = colors.shadow || colors.text;

  // Based on luminosity of theme colors, generate accessible colors to appear against them (e.g. for text or icons)
  const reverseColors = Object.entries(colors).reduce((obj, [key, value]) => {
    const hex = value.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    const luminosity = 0.299 * r + 0.587 * g + 0.114 * b; // Commonly used formula to derive perceived lightness

    return {
      ...obj,
      [key]: luminosity < 0.57 ? colors.textReverse : colors.text, // Preference for white text on our brand colors; using 0.57 takes it right up to the line of AA accessibility
    };
  }, {} as ThemeColors);

  const shadows = {
    regular: `0 2px 16px ${colors.shadow}22`,
    large: `0 2px 24px ${colors.shadow}22`,
    xlarge: `0 2px 32px ${colors.shadow}44`,
    tile: `0 4px ${colors.primary}20, 0 2px 40px ${colors.shadow}22`,
    tileSolid: `0 4px #DAD7EA, 0 2px 40px ${colors.shadow}22`,
    tileDark: `0 4px ${colors.primary}cc, 0 2px 24px ${colors.shadow}22`,
  };

  return {
    ...defaultTheme,
    fonts: { ...defaultTheme.fonts, ...baseTheme.fonts },
    fontWeights: { ...defaultTheme.fontWeights, ...baseTheme.fontWeights },
    colors,
    reverseColors,
    shadows,
  } as Theme;
};
